import FetchPermissionCatalog from '@Application/Security/Roles/Queries/FetchPermissionCatalog';
import FetchRoles from '@Application/Security/Roles/Queries/FetchRoles';
import fetchPermissionCatalog from '@Application/Security/Roles/fetchPermissionCatalog';
import fetchRoles from '@Application/Security/Roles/fetchRoles';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import RoleCard from '@UI/Pages/Administration/Roles/RoleCard';
import RoleTabs from '@UI/Pages/Administration/Roles/RoleTabs';
import selectedRoleIdSignal, {
	selectRole,
} from '@UI/Pages/Administration/Roles/Signals/selectedRoleIdSignal';
import { openRoleSidebar } from '@UI/Pages/Administration/Roles/Signals/roleSidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { Plus, ShieldQuestion } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton } from '@verclam/ui-kit';

const skeletonContainerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const skeletonTabsRowStyle = css`
	display: flex;
	gap: 0.5rem;
`;

const SKELETON_TAB_COUNT = 3;
const skeletonTabIndexes = [...Array(SKELETON_TAB_COUNT).keys()];

/**
 * A role selector (pill tabs) plus a single composition card, not a table — an organization
 * carries a handful of roles and the inventory asks for none of a table's filtering, sorting or
 * pagination. `rolesStore` and `permissionCatalogStore` are read in parallel; the card only renders
 * once both have resolved.
 */
const RolesContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user: currentUser } = useStrictAuthenticatedUser();
	const accountId = currentUser.account?.id ?? null;

	const selectedRoleId = useSignalValue(selectedRoleIdSignal);

	const {
		data: rolesList,
		isLoading: isRolesLoading,
		refetch: refetchRoles,
	} = useStoreQuery({
		selector: (state) => state.rolesStore.rolesList,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchRoles(new FetchRoles(accountId)));
		},
		deps: [accountId],
	});

	const {
		data: catalog,
		isLoading: isCatalogLoading,
		refetch: refetchCatalog,
	} = useStoreQuery({
		selector: (state) => state.permissionCatalogStore.catalog,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchPermissionCatalog(new FetchPermissionCatalog())),
		deps: [],
	});

	// Keeps the selection on a role that actually exists in the list just received: falls back to the
	// first role when nothing was selected yet, or when the previous selection was just deleted.
	React.useEffect(() => {
		if (rolesList === null || rolesList.list.length === 0) {
			return;
		}

		if (selectedRoleId !== null && rolesList.findById(selectedRoleId) !== null) {
			return;
		}

		const firstRole = rolesList.list[0];

		if (firstRole !== undefined) {
			selectRole(firstRole.id);
		}
	}, [rolesList, selectedRoleId]);

	const handleCreateClick = React.useCallback(() => {
		openRoleSidebar();
	}, []);

	const handleRetryClick = React.useCallback(() => {
		refetchRoles();
		refetchCatalog();
	}, [refetchRoles, refetchCatalog]);

	const createButton = (
		<Button type="button" label="Nouveau rôle" icon={<Plus size={16} />} onClick={handleCreateClick} />
	);

	if (isRolesLoading || isCatalogLoading) {
		return (
			<div css={skeletonContainerStyle}>
				<div css={skeletonTabsRowStyle}>
					{skeletonTabIndexes.map((index) => (
						<Skeleton
							key={index}
							width="7rem"
							height="2.25rem"
							borderRadius={theme.radii.pill}
							animation="wave"
						/>
					))}
				</div>
				<Skeleton
					height="18rem"
					borderRadius={theme.radii.xlarge}
					animation="wave"
					shape="rectangle"
				/>
			</div>
		);
	}

	if (rolesList === null || catalog === null) {
		return (
			<ErrorState
				title="Impossible de charger les rôles"
				description="Une erreur est survenue pendant le chargement des rôles ou du catalogue de permissions."
				onRetry={handleRetryClick}
			/>
		);
	}

	if (rolesList.list.length === 0) {
		return (
			<EmptyState
				title="Aucun rôle"
				description="Aucun rôle n'a encore été créé."
				icon={<ShieldQuestion size={28} />}
				action={createButton}
			/>
		);
	}

	const selectedRole = rolesList.findById(selectedRoleId) ?? rolesList.list[0];

	if (selectedRole === undefined) {
		return (
			<EmptyState
				title="Aucun rôle"
				description="Aucun rôle n'a encore été créé."
				icon={<ShieldQuestion size={28} />}
				action={createButton}
			/>
		);
	}

	return (
		<React.Fragment>
			<RoleTabs roles={rolesList.list} selectedRoleId={selectedRole.id} />
			<RoleCard role={selectedRole} catalog={catalog} />
		</React.Fragment>
	);
};

export default RolesContent;
